'use client';

import { useState, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';

import { useLocale, RealEstateDict } from '@monoimin/intl';

import { useParamsUpdater } from '@monoimin/hooks';

import { MIN_PRICE, MAX_PRICE, EMPTY_FILTERS_STATE } from '../../constants';

import {
  defineMainPageSubmitParams,
  defineMainPageSubmitPathname,
} from '../../utils/filters';

import { SelectRange } from '../SelectRange';

import { priceFormatter } from '../../utils/filters/priceFormatter';

import { createRangeFromFiltersStateValue } from '../../utils/filters/createRangeFromFiltersStateValue';

import { CountryFilter } from './CountryFilter';
import { CityFilter } from './CityFilter';

import type { FiltersData, FiltersState } from '../../types';

type RealEstateMainFiltersProps = Pick<FiltersData, 'countries' | 'cities'> & {
  className?: string;
  realEstateDict: RealEstateDict;
};

export const RealEstateMainFilters = ({
  className,
  countries,
  cities,
  realEstateDict,
}: RealEstateMainFiltersProps) => {
  const { onChange: onParamsChange } = useParamsUpdater();

  const locale = useLocale();

  const filterClassNames = {
    container: 'col-span-2',
  };

  const [localFilters, setLocalFilters] =
    useState<FiltersState>(EMPTY_FILTERS_STATE);

  const range = useMemo(() => {
    return createRangeFromFiltersStateValue(
      localFilters.price_from,
      localFilters.price_to
    );
  }, [localFilters.price_from, localFilters.price_to]);

  const onFiltersSubmit = useCallback(() => {
    const newParams = defineMainPageSubmitParams(localFilters);

    const newPathname = defineMainPageSubmitPathname(localFilters, locale);

    onParamsChange(newParams, newPathname);
  }, [localFilters, onParamsChange, locale]);

  return (
    <div className={clsx(className)}>
      <div className='p-7 grid grid-cols-7 gap-8'>
        <CountryFilter
          variant='desktop'
          countries={countries}
          filtersState={localFilters}
          onChange={(newState) => {
            setLocalFilters(newState);
          }}
          classNames={filterClassNames}
          realEstateDict={realEstateDict}
        />
        <CityFilter
          variant='desktop'
          cities={cities}
          countries={countries}
          filtersState={localFilters}
          onChange={(newState) => {
            setLocalFilters(newState);
          }}
          classNames={filterClassNames}
          realEstateDict={realEstateDict}
        />
        <SelectRange
          hideInput
          fromPlaceholder={realEstateDict.priceFrom}
          toPlaceholder={realEstateDict.priceTo}
          placeholder={realEstateDict.anyPrice}
          realEstateDict={realEstateDict}
          onChange={(value) => {
            if (Array.isArray(value)) {
              const [from, to] = value;

              setLocalFilters({
                ...localFilters,
                price_from: from,
                price_to: to,
              });
            }
          }}
          onReset={() => {
            setLocalFilters({
              ...localFilters,
              price_from: MIN_PRICE,
              price_to: MAX_PRICE,
            });
          }}
          min={MIN_PRICE}
          max={MAX_PRICE}
          value={range}
          classNames={filterClassNames}
          valueFormatter={priceFormatter}
        />
        <button
          onClick={onFiltersSubmit}
          className='button-primary duration-300'
        >
          {realEstateDict.show}
        </button>
      </div>
    </div>
  );
};
